import { useState, useMemo, useCallback } from "react";
import { Typography, Flex, InputNumber } from "antd";
import { useTranslate, useTranslation } from "@refinedev/core";
import {
  CreditCardIcon,
  UsersIcon,
  CheckCircleIcon,
} from "@phosphor-icons/react";
import {
  Modal,
  Button,
  ProgressBar,
  Spinner,
  Divider,
  AlertCard,
  radius,
} from "@libertify/ui";
import styled from "styled-components";
import { useGetActiveSubscriptions, useQuota } from "#src/hooks";
import { useGetProducts } from "../hooks/useGetProducts";
import { useUpdateSubscription } from "../hooks/useUpdateSubscription";
import { useSubscriptionInvoices } from "../hooks/useSubscriptionInvoices";
import { useStripePayment } from "../hooks/useStripePayment";
import { QuotaFeatures } from "@/constants";

const FooterWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const { Title, Text } = Typography;

interface AddSeatsModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  seatsNeeded?: number;
}

type ModalStep = "configure" | "payment" | "success" | "error";

export const AddSeatsModal = ({
  open,
  onCancel,
  onSuccess,
  seatsNeeded = 1,
}: AddSeatsModalProps) => {
  const t = useTranslate();
  const { getLocale } = useTranslation();
  const { subscriptions, refetch: refetchSubscription } =
    useGetActiveSubscriptions();
  const { getQuota, refetch: refetchQuota } = useQuota();
  const { products, isLoading: isLoadingProducts } = useGetProducts();
  const { updateSubscription, isPending: isUpdating } = useUpdateSubscription();
  const { getInvoices } = useSubscriptionInvoices(subscriptions?.id as string, {
    enabled: false,
  });
  const [step, setStep] = useState<ModalStep>("configure");
  const [additionalSeats, setAdditionalSeats] = useState(seatsNeeded);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [paymentClientSecret, setPaymentClientSecret] = useState<string | null>(
    null
  );

  const handlePaymentSuccess = useCallback(async () => {
    await Promise.all([refetchQuota(), refetchSubscription()]);
    setStep("success");
  }, [refetchQuota, refetchSubscription]);

  const {
    confirmPayment,
    isProcessing,
    isReady: isStripeReady,
  } = useStripePayment({
    onSuccess: () => {
      handlePaymentSuccess().catch((error) => {
        console.error("Error handling payment success", error);
      });
    },
    onError: (error) => {
      setErrorMessage(error);
      setStep("error");
    },
  });

  const seatQuota = getQuota(QuotaFeatures.UserLicenseAssignment);
  const currentSeats = seatQuota?.quota_limit ?? 0;
  const usedSeats = seatQuota?.usage ?? 0;

  // Find the subscribed product that has the seat feature
  const subscribedSeatProduct = useMemo(() => {
    if (!subscriptions?.subscribed_products) return null;
    return subscriptions.subscribed_products.find((product) =>
      product.features?.some(
        (f) => f.feature === QuotaFeatures.UserLicenseAssignment
      )
    );
  }, [subscriptions]);

  // Find the product definition for pricing using the subscribed product's product_id
  const seatProductDefinition = useMemo(() => {
    if (!subscribedSeatProduct) return null;
    return products.find(
      (product) => product.id === subscribedSeatProduct.product_id
    );
  }, [products, subscribedSeatProduct]);

  const pricePerSeat = useMemo(() => {
    if (!seatProductDefinition || !subscriptions) return null;
    const billingInterval = subscriptions.billing_interval ?? "month";
    const price = seatProductDefinition.prices.find(
      (p) => p.interval === billingInterval
    );
    return price;
  }, [seatProductDefinition, subscriptions]);

  const totalCost = useMemo(() => {
    if (!pricePerSeat) return null;
    const amount =
      typeof pricePerSeat.amount === "string"
        ? Number.parseFloat(pricePerSeat.amount)
        : pricePerSeat.amount;
    return {
      amount: amount * additionalSeats,
      currency: pricePerSeat.currency,
      interval: pricePerSeat.interval,
    };
  }, [pricePerSeat, additionalSeats]);

  // The subscription item ID is the Stripe si_xxx from the subscribed product
  const subscriptionItemId = subscribedSeatProduct?.id ?? null;

  async function handlePurchase() {
    if (!subscriptions?.id || !subscriptionItemId || !pricePerSeat) {
      setErrorMessage(t("addSeats.errors.missingSubscriptionInfo"));
      setStep("error");
      return;
    }

    try {
      const newQuantity = currentSeats + additionalSeats;

      // Step 1: Update the subscription
      await updateSubscription({
        subscriptionId: subscriptions.id as string,
        subscriptionItemId,
        priceId: pricePerSeat.id,
        quantity: newQuantity,
      });

      // Step 2: Retrieve invoices to check payment status
      const invoices = await getInvoices();

      // Find any invoice with "open" status that needs payment
      const openInvoice = invoices?.find(
        (inv) => inv.status === "open" && inv.payment_intent_client_secret
      );

      if (openInvoice) {
        // Invoice is open and needs payment - go to Stripe
        setPaymentClientSecret(
          openInvoice.payment_intent_client_secret ?? null
        );
        setStep("payment");
      } else {
        // All invoices are paid or no open invoices - success
        await handlePaymentSuccess();
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t("addSeats.errors.purchaseFailed");
      setErrorMessage(message);
      setStep("error");
    }
  }

  async function handleConfirmPayment() {
    if (!paymentClientSecret) return;
    await confirmPayment(paymentClientSecret);
  }

  function handleClose() {
    const wasSuccess = step === "success";

    // Reset state for next open
    setStep("configure");
    setAdditionalSeats(seatsNeeded);
    setErrorMessage(null);
    setPaymentClientSecret(null);

    if (wasSuccess) {
      onSuccess();
    } else {
      onCancel();
    }
  }

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat(getLocale(), {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount);
  };

  const isLoading = isLoadingProducts || isUpdating || isProcessing;

  const renderConfigureStep = () => (
    <Flex vertical gap={24}>
      <Flex
        style={{
          background: "#f5f5f5",
          borderRadius: radius.box,
          padding: 16,
        }}
        gap={16}
        align="center"
      >
        <UsersIcon size={32} weight="duotone" color="#1890ff" />
        <Flex vertical flex={1}>
          <Text type="secondary">{t("addSeats.currentSeats")}</Text>
          <Flex align="baseline" gap={8}>
            <Title level={4} style={{ margin: 0 }}>
              {usedSeats} / {currentSeats}
            </Title>
            <Text type="secondary">{t("addSeats.seatsUsed")}</Text>
          </Flex>
          <div style={{ marginTop: 8 }}>
            <ProgressBar
              percent={currentSeats > 0 ? (usedSeats / currentSeats) * 100 : 0}
              showInfo={false}
              color={usedSeats >= currentSeats ? "#ff4d4f" : "#1890ff"}
              height={8}
            />
          </div>
        </Flex>
      </Flex>

      <Flex vertical gap={8}>
        <Text strong>{t("addSeats.additionalSeatsLabel")}</Text>
        <InputNumber
          min={1}
          max={100}
          value={additionalSeats}
          onChange={(value) => setAdditionalSeats(value ?? 1)}
          style={{ width: "100%" }}
          size="large"
        />
      </Flex>

      {totalCost && pricePerSeat && (
        <>
          <Divider spacing="sm" />
          <Flex justify="space-between" align="center">
            <Text type="secondary">
              {t("addSeats.pricePerSeat", {
                price: formatCurrency(
                  typeof pricePerSeat.amount === "string"
                    ? parseFloat(pricePerSeat.amount)
                    : pricePerSeat.amount,
                  totalCost.currency
                ),
                interval: totalCost.interval,
              })}
            </Text>
          </Flex>
          <Flex
            justify="space-between"
            align="center"
            style={{
              background: "#e6f7ff",
              padding: 16,
              borderRadius: radius.box,
            }}
          >
            <Text strong style={{ fontSize: 16 }}>
              {t("addSeats.totalCost")}
            </Text>
            <Flex align="baseline" gap={4}>
              <Title level={3} style={{ margin: 0, color: "#1890ff" }}>
                {formatCurrency(totalCost.amount, totalCost.currency)}
              </Title>
              <Text type="secondary">/{totalCost.interval}</Text>
            </Flex>
          </Flex>
        </>
      )}

      <Text type="secondary">
        {t("addSeats.newTotal", { total: currentSeats + additionalSeats })}
      </Text>
    </Flex>
  );

  const renderPaymentStep = () => (
    <Flex vertical gap={24} align="center" style={{ padding: "24px 0" }}>
      <CreditCardIcon size={48} weight="duotone" color="#1890ff" />
      <Title level={4}>{t("addSeats.paymentRequired")}</Title>
      <Text type="secondary" style={{ textAlign: "center" }}>
        {t("addSeats.paymentDescription")}
      </Text>
      <Button
        variant="primary"
        size="l"
        onClick={() => {
          handleConfirmPayment().catch((error) => {
            console.error("Error handling confirm payment", error);
          });
        }}
        loading={isProcessing}
        disabled={!isStripeReady}
      >
        {t("addSeats.confirmPayment")}
      </Button>
    </Flex>
  );

  const renderSuccessStep = () => (
    <Flex vertical gap={24} align="center" style={{ padding: "24px 0" }}>
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: radius.circle,
          background: "#f6ffed",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CheckCircleIcon size={32} weight="bold" color="#52c41a" />
      </div>
      <Title level={4}>{t("addSeats.successTitle")}</Title>
      <Text type="secondary" style={{ textAlign: "center" }}>
        {t("addSeats.successDescription", { seats: additionalSeats })}
      </Text>
    </Flex>
  );

  const renderErrorStep = () => (
    <div style={{ marginBottom: 16 }}>
      <AlertCard
        type="error"
        title={t("addSeats.errorTitle")}
        description={errorMessage ?? undefined}
      />
    </div>
  );

  const getFooter = () => {
    if (step === "success") {
      return (
        <FooterWrapper>
          <Button variant="primary" onClick={handleClose}>
            {t("addSeats.done")}
          </Button>
        </FooterWrapper>
      );
    }

    if (step === "payment") {
      return (
        <FooterWrapper>
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={isProcessing}
          >
            {t("buttons.cancel")}
          </Button>
        </FooterWrapper>
      );
    }

    if (step === "error") {
      return (
        <FooterWrapper>
          <Button variant="secondary" onClick={onCancel}>
            {t("buttons.cancel")}
          </Button>
          <Button variant="primary" onClick={() => setStep("configure")}>
            {t("addSeats.tryAgain")}
          </Button>
        </FooterWrapper>
      );
    }

    return (
      <FooterWrapper>
        <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
          {t("buttons.cancel")}
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            handlePurchase().catch((error) => {
              console.error("Error handling purchase", error);
            });
          }}
          loading={isLoading}
          disabled={
            !subscribedSeatProduct || !pricePerSeat || additionalSeats < 1
          }
        >
          {t("addSeats.purchaseButton")}
        </Button>
      </FooterWrapper>
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t("addSeats.title")}
      footer={getFooter()}
      size="medium"
      destroyOnHidden
    >
      {isLoading && step === "configure" ? (
        <Flex justify="center" align="center" style={{ padding: "48px 0" }}>
          <Spinner size="lg" />
        </Flex>
      ) : (
        <>
          {step === "configure" && renderConfigureStep()}
          {step === "payment" && renderPaymentStep()}
          {step === "success" && renderSuccessStep()}
          {step === "error" && (
            <>
              {renderErrorStep()}
              {renderConfigureStep()}
            </>
          )}
        </>
      )}
    </Modal>
  );
};
